/**
 * The recipient's page for one feedback request.
 *
 * The card in the middle is `FeedbackRequestRespond` verbatim — the same
 * component the transcript widget and the desktop Inbox render. Everything this
 * file adds is the chrome around it and the decision about which of the cold
 * link's several endings the reader is looking at.
 *
 * The `discussion` slot is filled with `FeedbackRequestDiscussion` — the
 * browser's own thinner comment renderer. The respond tree knows nothing about
 * any comment system; it renders whatever node it is handed.
 */

import type { ReactNode } from 'react';
import { EditorBreadcrumb, EditorHeaderBar } from '@nimbalyst/collab-bundle/docs-ui';
import { FeedbackRequestRespond } from '@nimbalyst/collab-bundle/feedback-ui';
import type { FeedbackRespondHost } from '@nimbalyst/collab-bundle/feedback-ui';
import type {
  FeedbackArtifact,
  FeedbackRequestLifecycleStatus,
} from '@nimbalyst/collab-protocol';

import { desktopFeedbackRequestUrl } from '../auth/desktopHandoff';
import { PanelState } from '../components/Chrome';
import { BUTTON_PRIMARY_LG } from '../controlStyles';
import { DocsSurfaceError } from '../routes/DocsRoutes';
import type { BrowserFeedbackRequestState } from './BrowserFeedbackRequestClient';
import {
  FeedbackRequestDiscussion,
  type FeedbackDiscussionPost,
} from './FeedbackRequestDiscussion';
import { resolveFeedbackRequestSurface } from './feedbackRequestSurface';
import type { FeedbackArtifactPreviews } from './useFeedbackArtifactPreviews';

const LIFECYCLE_COPY: Record<
  Exclude<FeedbackRequestLifecycleStatus, 'open'>,
  { title: string; message: string }
> = {
  closed: {
    title: 'Closed before you answered',
    message: 'This request is no longer taking answers. The questions are still readable.',
  },
  expired: {
    title: 'The deadline passed',
    message: 'This request expired before you answered. The questions are still readable.',
  },
  cancelled: {
    title: 'Withdrawn by whoever asked',
    message: 'This request was cancelled. The questions are still readable.',
  },
};

function whenChanged(changedAt: number): string {
  return new Date(changedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export interface FeedbackRequestPageProps {
  orgName: string;
  orgId: string;
  requestId: string;
  state: BrowserFeedbackRequestState;
  /** Absent for the frame before the connection exists; the card says so. */
  host?: FeedbackRespondHost;
  /** Absent for the same frame; the discussion then reads without a composer. */
  postComment?: FeedbackDiscussionPost;
  /** Routes to a subject's shared document; the respond tree owns no routing. */
  onOpenSubject?: (subject: FeedbackArtifact) => void;
  /**
   * Live artifact painting. Absent -- an org whose project could not be named,
   * or a request with no document artifacts -- and the cards keep their titled
   * placeholders and expand keeps opening a tab.
   */
  previews?: FeedbackArtifactPreviews;
  /** Revoke and re-run the sign-in round trip; the session is what expired. */
  onSignIn: () => void;
}

const DISCUSSION_READ_ONLY_REASON: Record<
  Exclude<FeedbackRequestLifecycleStatus, 'open'>,
  string
> = {
  closed: 'This request is closed, so the discussion is read-only.',
  expired: 'This request expired, so the discussion is read-only.',
  cancelled: 'This request was cancelled, so the discussion is read-only.',
};

export function FeedbackRequestPage({
  orgName,
  orgId,
  requestId,
  state,
  host,
  postComment,
  onSignIn,
  onOpenSubject,
  previews,
}: FeedbackRequestPageProps) {
  const surface = resolveFeedbackRequestSurface(state);
  const request = state.request;
  const addressedToViewer = surface.kind === 'respond' || surface.kind === 'read-only';
  // Subjects now carry the author's label, so the thing the request is *about*
  // is a better caption than the first question about it; the ask label stays
  // as the fallback for a request with no subject. Either way only someone the
  // request is addressed to gets it — a bystander who can read the room must
  // not be handed the question in the breadcrumb.
  const caption = addressedToViewer
    ? request?.subjects?.[0]?.label ?? request?.asks[0]?.label
    : undefined;

  let body: ReactNode;
  switch (surface.kind) {
    case 'loading':
      body = <PanelState tone="loading">Opening this request…</PanelState>;
      break;
    case 'not-signed-in':
      body = (
        <div className="feedback-request-recovery grid justify-items-center gap-4">
          <DocsSurfaceError
            kind="session-expired"
            detail="Sign in again to read this request and answer it."
          />
          <button className={BUTTON_PRIMARY_LG} type="button" onClick={onSignIn}>Sign in again</button>
        </div>
      );
      break;
    case 'not-authorized':
      body = (
        <DocsSurfaceError
          kind="removed-member"
          detail="Your membership of this organization is not active, so this request cannot be opened."
        />
      );
      break;
    case 'unavailable':
      body = <DocsSurfaceError kind="service-unavailable" detail={surface.message} />;
      break;
    case 'not-a-recipient':
      // No action, by design. There is nothing this reader can do and nothing
      // they should be told about a request that is not theirs.
      body = (
        <PanelState tone="empty" className="docs-state-panel grid gap-1.5" data-docs-state="not-a-recipient">
          <strong className="text-[1.05rem]">This request is not addressed to you</strong>
          <span className="text-nim-muted">Nothing on it is assigned to you, so there is nothing here to answer.</span>
        </PanelState>
      );
      break;
    case 'read-only':
    case 'respond':
      body = (
        <>
          {surface.kind === 'read-only' && request && (
            <section
              className="feedback-request-lifecycle grid gap-[0.3rem] rounded-[0.6rem] border border-nim bg-nim-secondary px-4 py-[0.85rem]"
              data-lifecycle-status={surface.status}
            >
              <strong className="text-[0.9rem] text-nim">{LIFECYCLE_COPY[surface.status].title}</strong>
              <span className="text-[0.85rem] leading-normal text-nim-muted">{LIFECYCLE_COPY[surface.status].message}</span>
              <small className="text-[0.78rem] text-nim-faint">{whenChanged(request.lifecycle.changedAt)}</small>
            </section>
          )}
          <FeedbackRequestRespond
            state={state}
            host={host}
            onOpenSubject={onOpenSubject}
            renderOptionPreview={previews?.renderOptionPreview}
            renderSubjectPreview={previews?.renderSubjectPreview}
            renderArtifactDetail={previews?.renderArtifactDetail}
            discussion={request && (
              <FeedbackRequestDiscussion
                comments={request.discussion}
                viewerUserId={state.teamMemberId}
                recipients={request.recipients}
                // The server refuses a comment on a terminal request through
                // the same lifecycle gate it refuses an answer with, so a
                // composer here would be an affordance guaranteed to fail.
                post={surface.kind === 'respond' ? postComment : undefined}
                readOnlyReason={surface.kind === 'read-only'
                  ? DISCUSSION_READ_ONLY_REASON[surface.status]
                  : undefined}
              />
            )}
          />
        </>
      );
      break;
  }

  return (
    <section className="feedback-request-page flex h-full min-h-0 flex-col overflow-hidden" data-feedback-surface={surface.kind}>
      <EditorHeaderBar
        breadcrumb={(
          <EditorBreadcrumb
            crumbs={[
              { id: 'org', label: orgName },
              ...(caption
                ? [
                  { id: 'requests', label: 'Requests' },
                  { id: 'caption', label: caption, current: true },
                ]
                : [{ id: 'requests', label: 'Requests', current: true }]),
            ]}
          />
        )}
        actions={(
          <>
            {/* An offer, not a redirect: answering in the browser is supported. */}
            <a
              className="feedback-request-handoff shrink-0 whitespace-nowrap rounded border border-nim px-2 py-0.5 text-[0.78rem] text-nim-muted no-underline hover:border-nim-focus hover:text-nim"
              href={desktopFeedbackRequestUrl(orgId, requestId)}
            >
              Open in Nimbalyst
            </a>
          </>
        )}
      />
      <div className="feedback-request-scroll min-h-0 flex-1 overflow-y-auto p-[clamp(1.25rem,3vw,2.5rem)]">
        <div className="feedback-request-column mx-auto grid w-[min(46rem,100%)] gap-4 select-text">{body}</div>
      </div>
    </section>
  );
}
